import { Tooltip, Typography } from "antd";
import { ExpolitedIconV2, ExternalLinkIcon } from "components/Icons";
import InternalLink from "components/InternalLink";
import Vocabulary, { getVocabulary } from "components/Vocabulary";
import { PermissionSetMap } from "constants/RBACConstants";
import _, { isNil } from "lodash";
import React, { ReactNode } from "react";
import { FindingSummary } from "types/api/findingSummary";
import { hasPermission } from "utils/authService";
import { getColorAndSeverityByScore } from "utils/cvssUtil";
import { setLocalStorage } from "utils/localStorageUtils";
import { CellRenderers } from "utils/tableUtils";
import { ArmorcodeCategory } from "views/Findings/ArmorcodeCategory";
import { UNASSIGNED } from "views/Findings/ChangeOwner";
import {
  isCustomFinding,
  renderLastResolutionNote,
} from "views/Findings/utils";
import EnvironmentName from "views/GlobalSettings/EnvironmentName";
import { SeverityDropDown } from "views/Policy/Policy";

import ACLink from "ac-components/ACLink/ACLink";
import ACSeverityTag from "ac-components/ACSeverityTag";
import CopyIcon from "ac-components/ac-icons/CopyIcon";
import LateIcon from "ac-components/ac-icons/LateIcon";
import MetIcon from "ac-components/ac-icons/MetIcon";
import ACResponsiveTags from "ac-components/ac-responsive-tags/ACResponsiveTags";
import ACTagColored from "ac-components/ac-tag-colored/ACTagColored";

import FindingAssetScoreFormula from "../FindingAssetScoreFormula";
import FindingScoreFormula from "../FindingScoreFormula";
import FindingStatusDropdown from "../FindingStatusDropdown";
import RiskTagsDisplay from "../RiskTagsDisplay";
import { PinnableFieldTypes } from "./PinnableFieldTypes";

const { Link } = Typography;

type GsccAdditionalDetails = {
  gscc_observedinthewild?: boolean | string | null;
  gscc_exploitationactivity?: string | null;
  gscc_zeroday?: boolean | string | null;
  gscc_upstream_fix_available?: boolean | string | null;
};

const GSCC_FIELD_NAMES = [
  "gscc_observedinthewild",
  "gscc_exploitationactivity",
  "gscc_zeroday",
  "gscc_upstream_fix_available",
] as const;

export const isGsccEnrichedFinding = (finding?: FindingSummary) => {
  const additionalDetails = finding?.additionalDetails as
    | GsccAdditionalDetails
    | undefined;

  return GSCC_FIELD_NAMES.some(
    (fieldName) => fieldName in (additionalDetails ?? {}),
  );
};

export const formatGSCCFieldValue = (
  value: boolean | string | null | undefined,
) => {
  if (_.isNil(value) || value === "") {
    return CellRenderers.VALUE_OR_NA();
  }

  if (_.isBoolean(value)) {
    return _.capitalize(String(value));
  }

  return _.capitalize(value);
};

export const SeverityField: PinnableFieldTypes = {
  name: "severity",
  path: "severity",
  label: <Vocabulary name="severity" />,
  formatter: (severity, finding, onSelect) => {
    if (hasPermission(PermissionSetMap.UPDATE_SEVERITY)) {
      return (
        <SeverityDropDown
          severity={severity}
          onSelect={onSelect}
          isCustomized={true}
        />
      );
    }
    return <ACSeverityTag value={severity} />;
  },
  group: "general",
  span: 24,
};

export const CategoryField: PinnableFieldTypes = {
  name: "category",
  label: <Vocabulary name="category" />,
  path: "category",
  formatter: (category, finding: FindingSummary, onSelect) => {
    if (hasPermission(PermissionSetMap.UPDATE_SEVERITY)) {
      // TODO: define type for ArmorcodeCategory
      // @ts-expect-error - need to define types
      return <ArmorcodeCategory category={category} onChange={onSelect} />;
    }
    return CellRenderers.VALUE_OR_NA(category);
  },
  group: "general",
  span: 24,
};

export const FindingScoreField: PinnableFieldTypes = {
  label: <Vocabulary name="findingScore" />,
  path: "findingScore",
  name: "findingScore",
  group: "riskScore",
  span: 24,
  formatter: (score: number, finding: FindingSummary) => (
    <FindingScoreFormula
      findingScore={finding?.findingScore}
      severity={finding?.severity}
      status={finding?.status}
      additionalDetails={finding?.additionalDetails}
      cvssBaseScore={finding?.cvssBaseScore}
      cves={finding?.cves}
      aatiScore={finding?.aatiScore}
      toolName={finding?.toolName}
    />
  ),
};

export const StatusField: PinnableFieldTypes = {
  label: "Status",
  path: "status",
  name: "status",
  group: "general",
  formatter: () => <FindingStatusDropdown />,
  span: 24,
};

export const EngagementField: PinnableFieldTypes = {
  label: <Vocabulary name="project" />,
  path: "armorcodeProjects",
  formatter: (projects: [{ name: string; id: string }]) => {
    if (projects) {
      const projectNames = projects.map((project) => project.name);
      return (
        <ACResponsiveTags
          tags={projectNames}
          itemRenderer={(project) => {
            const projectId = projects.find((p) => p.name === project)?.id;
            return (
              <InternalLink
                to={`/project/${projectId}`}
                component={(props) => (
                  <ACLink
                    {...props}
                    primary={false}
                    overlay
                    onClick={(e) => e.stopPropagation()}
                  />
                )}
              >
                {project}
              </InternalLink>
            );
          }}
        />
      );
    }
  },
  name: "armorcodeProjects",
  group: "general",
  span: 24,
};

export const ComplianceField: PinnableFieldTypes = {
  label: "Compliance",
  path: "policy",
  name: "compliance",
  group: "general",
  span: 24,
  formatter: (values = []) => <ACResponsiveTags tags={values} />,
};

export const AMCRuleField: PinnableFieldTypes = {
  label: "AMC Rules",
  path: "ruleKey",
  formatter: (value: string[]) => <ACResponsiveTags tags={value} />,
  actionPermission: PermissionSetMap.UPDATE_FINDINGS,
  name: "ruleKey",
  group: "general",
  span: 24,
};

export const AATIScoreField: PinnableFieldTypes = {
  label: "AATI",
  path: "additionalDetails.aatiScore",
  name: "aatiScore",
  group: "vulnerability",
  span: 24,
  formatter: (score: number, finding: FindingSummary) => {
    score = CellRenderers.VALUE_OR_NA(score);
    if (
      finding?.additionalDetails?.cveUsed &&
      score !== CellRenderers.VALUE_OR_NA()
    ) {
      // Set activeTab to CSW in URL params (required in new version, was default in old version)
      const filters = { activeTab: ["CSW"] };
      const encodedFilters = encodeURIComponent(JSON.stringify(filters));
      return (
        <Typography.Link
          target="_blank"
          href={`#/feeds/${finding.additionalDetails.cveUsed}?filters=${encodedFilters}`}
        >
          {/* TODO: define types for ExternalLinkIcon */}
          {/* @ts-expect-error - need to define types */}
          {score} <ExternalLinkIcon />
        </Typography.Link>
      );
    }
    return score;
  },
};

export const ExploitedField: PinnableFieldTypes = {
  label: "Exploit Available",
  path: "additionalDetails.exploited",
  name: "exploited",
  group: "vulnerability",
  span: 24,
  actionPermission: PermissionSetMap.UPDATE_FINDINGS,
  formatter: (value: string) =>
    _.isNil(value) ? CellRenderers.VALUE_OR_NA() : _.capitalize(value),
};

export const CISAKEVField: PinnableFieldTypes = {
  label: "CISA KEV",
  path: "additionalDetails.isCISAKEV",
  name: "isCISAKEV",
  group: "vulnerability",
  span: 24,
  actionPermission: PermissionSetMap.UPDATE_FINDINGS,
  formatter: (value: string) =>
    _.isNil(value) ? CellRenderers.VALUE_OR_NA() : _.capitalize(value),
};

export const GSCCObservedInTheWildField: PinnableFieldTypes = {
  label: "Observed In The Wild",
  path: "additionalDetails.gscc_observedinthewild",
  name: "gsccObservedInTheWild",
  group: "vulnerability",
  span: 24,
  formatter: formatGSCCFieldValue,
};

export const GSCCExploitationActivityField: PinnableFieldTypes = {
  label: "Exploitation Activity",
  path: "additionalDetails.gscc_exploitationactivity",
  name: "gsccExploitationActivity",
  group: "vulnerability",
  span: 24,
  formatter: CellRenderers.VALUE_OR_NA,
};

export const GSCCZeroDayField: PinnableFieldTypes = {
  label: "Zero Day",
  path: "additionalDetails.gscc_zeroday",
  name: "gsccZeroDay",
  group: "vulnerability",
  span: 24,
  formatter: formatGSCCFieldValue,
};

export const GSCCUpstreamFixAvailableField: PinnableFieldTypes = {
  label: "Upstream Fix Available",
  path: "additionalDetails.gscc_upstream_fix_available",
  name: "gsccUpstreamFixAvailable",
  group: "vulnerability",
  span: 24,
  formatter: formatGSCCFieldValue,
};

export const OldCVSSBaseScoreField: PinnableFieldTypes = {
  label: "Base Score",
  path: "baseScore",
  name: "cvssBaseScore",
  group: "vulnerability",
  span: 24,
  actionPermission: PermissionSetMap.UPDATE_FINDINGS,
};

export const CVSSBaseScoreField: PinnableFieldTypes = {
  label: "CVSS",
  path: "baseScore",
  name: "cvssBaseScore",
  group: "vulnerability",
  span: 24,
  containerClassName: "flex-column",
  actionPermission: PermissionSetMap.UPDATE_FINDINGS,
  formatter: (
    baseScore: number | null,
    finding,
    editActionButton: ReactNode,
  ) => {
    return (
      <BaseScoreRender finding={finding} editActionButton={editActionButton} />
    );
  },
  hideDefaultEditAction: true,
};

const BaseScoreRender = ({
  finding,
  editActionButton,
}: {
  finding?: FindingSummary;
  editActionButton: ReactNode;
}) => {
  if (!finding) {
    return null;
  }
  const baseScore = finding.baseScore;

  const overallScore = (
    finding.additionalDetails?.cvssOverallScore
      ? finding.additionalDetails?.cvssOverallScore
      : null
  ) as number | null;

  return (
    <div className="d-flex w-100">
      <div className="flex-1">
        <div className="d-flex align-items-center" style={{ gap: 6 }}>
          <div>
            <div className="text-muted">Base Score</div>
            <div>
              <ACTagColored
                dot={false}
                color={getColorAndSeverityByScore(baseScore || undefined).color}
                label={!isNil(baseScore) ? `${baseScore}` : "-"}
              />
            </div>
          </div>
          {/* @ts-expect-error Property 'testType' does not exist on type 'FindingSummary'. */}
          {isCustomFinding(finding?.testType) ? editActionButton : null}
        </div>
      </div>
      <div className="flex-1">
        <div className="text-muted">Overall Score</div>
        <div>
          <ACTagColored
            dot={false}
            color={getColorAndSeverityByScore(overallScore || undefined).color}
            label={!isNil(overallScore) ? `${overallScore}` : "-"}
          />
        </div>
      </div>
    </div>
  );
};

export const CVSSVectorField: PinnableFieldTypes = {
  label: "CVSS Vector",
  path: "cvssVector",
  name: "cvssVector",
  group: "vulnerability",
  containerClassName: "flex-column",
  actionPermission: PermissionSetMap.UPDATE_FINDINGS,
  icon: (finding: FindingSummary | undefined) => {
    return finding?.cvssVector ? (
      <Typography.Text
        copyable={{ text: finding.cvssVector, icon: <CopyIcon /> }}
      />
    ) : null;
  },
  //   TODO: resolve TS error with formatter
  //   @ts-expect-error - Callback parameter is optional but typed as required
  formatter: (
    vector: string,
    finding: FindingSummary,
    showCVSSVectorComponents: () => void,
  ) =>
    vector ? (
      <Link
        className="w-100"
        title={vector}
        onClick={showCVSSVectorComponents}
        data-value={vector}
        ellipsis
      >
        {vector}
      </Link>
    ) : (
      CellRenderers.VALUE_OR_NA()
    ),
};

export const ImpactScoreField: PinnableFieldTypes = {
  label: "Impact score",
  path: "impactScore",
  name: "impactScore",
  group: "vulnerability",
  span: 24,
};

export const ExploitabilityScoreField: PinnableFieldTypes = {
  label: "Exploitability score",
  path: "exploitabilityScore",
  name: "exploitabilityScore",
  group: "vulnerability",
  span: 24,
};

export const CVEField: PinnableFieldTypes = {
  label: "CVE",
  path: "cves",
  name: "cve",
  group: "vulnerability",
  editButtonClassName: "cve-edit-action",
  span: 24,
  formatter: (values: string[], finding: FindingSummary) => {
    const cves = _.uniq(
      _.compact([finding?.additionalDetails?.cveUsed, ...(values ?? [])]),
    ) as string[];
    const findingId = finding?.id;
    const exploitedVulnerabilities = String(
      finding?.additionalDetails?.exploitedVulnerabilities ?? "",
    );
    const exploitedVulArr = exploitedVulnerabilities?.split(",");

    return (
      <ACResponsiveTags
        tags={cves}
        itemRenderer={(cve) => {
          const isExploited = exploitedVulArr.includes(cve);
          return (
            <>
              {isExploited && (
                <ExpolitedIconV2 className="font-size-sm m-r-xs" />
              )}
              <ACLink
                target="_blank"
                href={`/compliance/vulnerability-rules/${cve}/redirect?findingId=${findingId}`}
                primary={false}
              >
                {cve}
              </ACLink>
            </>
          );
        }}
      />
    );
  },
  actionPermission: PermissionSetMap.UPDATE_FINDINGS,
};

export const CWEField: PinnableFieldTypes = {
  label: "CWE",
  path: "cwesStrings",
  name: "cwe",
  group: "vulnerability",
  span: 24,
  formatter: (values: string[]) => {
    const filteredValues = _.filter(
      values,
      (text): text is string => Boolean(text) && text !== "0",
    );
    return (
      <ACResponsiveTags
        tags={filteredValues}
        itemRenderer={(cwe) => (
          <ACLink
            target="_blank"
            href={`https://cwe.mitre.org/data/definitions/${cwe.replace(
              "CWE-",
              "",
            )}.html`}
            primary={false}
          >
            {cwe}
          </ACLink>
        )}
      />
    );
  },
  actionPermission: PermissionSetMap.UPDATE_FINDINGS,
};

export const FoundOnField: PinnableFieldTypes = {
  label: "Found On",
  path: "foundOn",
  name: "foundOn",
  dateType: "other",
  group: "timeline",
  span: 24,
  formatter: CellRenderers.DATE,
};

export const DueOnField: PinnableFieldTypes = {
  label: (
    <>
      <Vocabulary name="resolution" /> Due Date
    </>
  ),
  path: "slaDueDate",
  name: "slaDueDate",
  group: "dates",
  span: 24,
  actionPermission: PermissionSetMap.FINDINGS_CHANGE_DUE_DATE,
  formatter: (value: number, finding: FindingSummary) => (
    <Tooltip
      title={
        <>
          Changed:
          {renderLastResolutionNote(finding?.lastResolutionNote)}
        </>
      }
    >
      <span>{CellRenderers.DATE(value)}</span>
    </Tooltip>
  ),
};

export const SLADueOnField: PinnableFieldTypes = {
  ...DueOnField,
  label: () => `${getVocabulary("resolution")} Due Date`,
  dateType: "sla",
  group: "timeline",
};

export const RemediationField: PinnableFieldTypes = {
  label: () => `${getVocabulary("remediation")} Due Date`,
  path: "remediationDueDate",
  name: "remediationDueDate",
  dateType: "sla",
  group: "timeline",
  span: 24,
  icon: (data: FindingSummary) =>
    getSLAFieldStatusIcon(data, "remediationSlaStatus"),
  formatter: CellRenderers.DATE,
};

export const TriageField: PinnableFieldTypes = {
  label: () => `${getVocabulary("triage")} Due Date`,
  path: "triageDueDate",
  name: "triageDueDate",
  dateType: "sla",
  group: "timeline",
  span: 24,
  icon: (data: FindingSummary) =>
    getSLAFieldStatusIcon(data, "triageSlaStatus"),
  formatter: CellRenderers.DATE,
};

export const LastSeenField: PinnableFieldTypes = {
  label: "Last Seen",
  path: "lastSeenDate",
  formatter: CellRenderers.DATE,
  span: 24,
  name: "lastSeenDate",
  dateType: "other",
  group: "timeline",
};

export const LastChangedField: PinnableFieldTypes = {
  label: "Last Changed",
  path: "lastUpdatedAt",
  formatter: CellRenderers.TIME_RELATIVE,
  span: 24,
  name: "lastUpdatedAt",
  dateType: "other",
  group: "timeline",
};

export const LastMitigatedField: PinnableFieldTypes = {
  label: () => `Last ${getVocabulary("mitigated")}`,
  path: "mitigatedAt",
  formatter: CellRenderers.TIME_RELATIVE,
  span: 24,
  name: "mitigatedAt",
  dateType: "other",
  group: "timeline",
};

export const LastTriagedField: PinnableFieldTypes = {
  label: () => `Last ${getVocabulary("triageStatus")}`,
  path: "lastTriagedDate",
  formatter: CellRenderers.TIME_RELATIVE,
  span: 24,
  name: "lastTriagedDate",
  dateType: "other",
  group: "timeline",
};

export const LastConfirmedField: PinnableFieldTypes = {
  label: () => `Last ${getVocabulary("confirmed")}`,
  path: "lastConfirmDate",
  formatter: CellRenderers.TIME_RELATIVE,
  span: 24,
  name: "lastConfirmDate",
  dateType: "other",
  group: "timeline",
};

export const LatestToolScanDateField: PinnableFieldTypes = {
  label: "Latest Tool Scan Date",
  path: "lastScanDate",
  formatter: CellRenderers.DATE,
  span: 24,
  name: "lastScanDate",
  dateType: "other",
  group: "timeline",
};

export const CVEPublishedDateField: PinnableFieldTypes = {
  label: "CVE Published Date",
  path: "cvePublishedDate",
  formatter: CellRenderers.DATE,
  span: 24,
  name: "cvePublishedDate",
  dateType: "other",
  group: "vulnerability",
};

export const CVELastModifiedDateField: PinnableFieldTypes = {
  label: "CVE Last Modified Date",
  path: "cveLastModifiedDate",
  formatter: CellRenderers.DATE,
  span: 24,
  name: "cveLastModifiedDate",
  dateType: "other",
  group: "vulnerability",
};

export const LastFalsePositiveField: PinnableFieldTypes = {
  label: () => `Last ${getVocabulary("falsePositive")}`,
  path: "lastFalsePositiveDate",
  formatter: CellRenderers.TIME_RELATIVE,
  span: 24,
  name: "lastFalsePositiveDate",
  dateType: "other",
  group: "timeline",
};

export const LastOpenedField: PinnableFieldTypes = {
  label: () => `Last ${getVocabulary("open")}`,
  path: "lastOpenDate",
  formatter: CellRenderers.TIME_RELATIVE,
  span: 24,
  name: "lastOpenDate",
  dateType: "other",
  group: "timeline",
};

export const LastAcceptRiskField: PinnableFieldTypes = {
  label: () => `Last ${getVocabulary("acceptRisk")}`,
  path: "lastAcceptRiskDate",
  formatter: CellRenderers.TIME_RELATIVE,
  span: 24,
  name: "lastAcceptRiskDate",
  dateType: "other",
  group: "timeline",
};

export const LastSuppressedField: PinnableFieldTypes = {
  label: () => `Last ${getVocabulary("suppressed")}`,
  path: "lastSuppressDate",
  formatter: CellRenderers.TIME_RELATIVE,
  span: 24,
  name: "lastSuppressDate",
  dateType: "other",
  group: "timeline",
};

export const OwnerField: PinnableFieldTypes = {
  label: <Vocabulary name="owner" />,
  path: "owner.name",
  actionPermission: PermissionSetMap.ASSIGN_OWNER,
  formatter: (owner) => owner ?? UNASSIGNED,
  span: 24,
  name: "owner",
  group: "owner",
};

export const BusinessOwnerField: PinnableFieldTypes = {
  label: <Vocabulary name="businessOwner" />,
  path: "businessOwner.name",
  span: 24,
  name: "businessOwner",
  group: "owner",
};

export const SecurityOwnerField: PinnableFieldTypes = {
  label: <Vocabulary name="securityOwner" />,
  path: "securityOwner.name",
  span: 24,
  name: "securityOwner",
  group: "owner",
};

export const EngineeringOwnerField: PinnableFieldTypes = {
  label: <Vocabulary name="engineeringOwner" />,
  path: "engineeringOwner.name",
  span: 24,
  name: "engineeringOwner",
  group: "owner",
};

export const SupportOwnerField: PinnableFieldTypes = {
  label: <Vocabulary name="supportOwner" />,
  path: "supportOwner.name",
  span: 24,
  name: "supportOwner",
  group: "owner",
};

export const CompainceOwnerField: PinnableFieldTypes = {
  label: <Vocabulary name="complianceOwner" />,
  path: "complianceOwner.name",
  span: 24,
  name: "complianceOwner",
  group: "owner",
};

export const TeamField: PinnableFieldTypes = {
  label: <Vocabulary name="team" />,
  path: "team.name",
  span: 24,
  name: "team",
  group: "owner",
};

export const AssetScoreField: PinnableFieldTypes = {
  label: <Vocabulary name="assetScore" />,
  path: "assetScore",
  span: 24,
  name: "assetScore",
  group: "riskScore",
  relatedFields: ["assetScoreTags"],
  formatter: (score, finding) => <FindingAssetScoreFormula finding={finding} />,
};

export const AssetScoreExtraField: PinnableFieldTypes = {
  label: "",
  path: "assetScore",
  name: "assetScoreTags",
  group: "riskScore",
  span: 24,
  formatter: (assetScore, finding) => {
    return <RiskTagsDisplay tags={finding?.tagsUsedForAssetScore} />;
  },
};

export const CTIScoreField: PinnableFieldTypes = {
  label: "CTI Score",
  path: "additionalDetails.ctiScore",
  name: "ctiScore",
  span: 24,
  formatter: (score, finding) =>
    score ? (
      <Typography.Link
        // @ts-expect-error - threatSourceUrl is object but href needs string
        href={finding?.additionalDetails?.threatSourceUrl}
        target="_blank"
      >
        {score}
      </Typography.Link>
    ) : (
      CellRenderers.VALUE_OR_NA()
    ),
  group: "vulnerability",
};

export const ThreatSourceField: PinnableFieldTypes = {
  label: "Threat Source",
  name: "threatSource",
  path: "additionalDetails.threatSource",
  span: 24,
  formatter: CellRenderers.VALUE_OR_NA,
  group: "vulnerability",
};

export const updateEnvInLocalStorage = (env: string) => {
  setLocalStorage("dashboardEnv", env);
};

export const ProductField: PinnableFieldTypes = {
  label: <Vocabulary name="product" />,
  name: "product",
  path: "product",
  span: 24,
  formatter: (productMap, finding) =>
    productMap?.id ? (
      <ACLink
        overlay
        primary={false}
        href={`/#/product/${productMap?.id}`}
        target="_blank"
        onClick={() => updateEnvInLocalStorage(finding.envName ?? "")}
        title={productMap?.name}
      >
        {productMap?.name}
      </ACLink>
    ) : (
      CellRenderers.VALUE_OR_NA()
    ),
  group: "scope",
};
export const SubProductField: PinnableFieldTypes = {
  label: <Vocabulary name="subProduct" />,
  name: "subProduct",
  path: "subProduct",
  span: 24,
  formatter: (subProductMap, finding: FindingSummary) => {
    return (
      <ACLink
        overlay
        primary={false}
        href={`/#/subProduct/${subProductMap?.id}`}
        target="_blank"
        onClick={() => updateEnvInLocalStorage(finding.envName ?? "")}
        title={subProductMap?.name}
      >
        {subProductMap?.name}
      </ACLink>
    );
  },
  group: "scope",
};

export const EnvField: PinnableFieldTypes = {
  label: "Environment",
  name: "envName",
  path: "envName",
  span: 24,
  formatter: (environmentName) => <EnvironmentName name={environmentName} />,
  group: "scope",
};

export const getSLAFieldStatusIcon = (
  data: FindingSummary,
  fieldName: string,
) => {
  // @ts-expect-error - FindingSummary lacks string index signature
  const fieldValue = data?.[fieldName];
  if (fieldValue === "FAIL") {
    return (
      <Tooltip title="SLA Status Failed">
        <LateIcon />
      </Tooltip>
    );
  }
  if (fieldValue === "PASS") {
    return (
      <Tooltip title="SLA Status Passed">
        <MetIcon />
      </Tooltip>
    );
  }
  return null;
};
